import React, { useState, useEffect } from 'react';
import { X, Share2, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { shareProject, getProjectAccessList, revokeProjectAccess } from '../services/cloudFirstSync';
import './ShareProjectModal.css';

const ShareProjectModal = ({ project, onClose, onShareSuccess }) => {
  const [email, setEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [accessList, setAccessList] = useState([]);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [revokingAccess, setRevokingAccess] = useState(null);

  useEffect(() => {
    if (project) {
      loadAccessList();
    }
  }, [project]);

  const loadAccessList = async () => {
    if (!project?.id) return;
    
    setIsLoadingAccess(true);
    try {
      const access = await getProjectAccessList(project.id);
      setAccessList(access);
    } catch (error) {
      console.error('Error loading access list:', error);
    } finally {
      setIsLoadingAccess(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setShareError('Please enter an email address');
      return;
    }

    if (!project?.id) {
      setShareError('Project not found');
      return;
    }

    setIsSharing(true);
    setShareError('');
    setShareSuccess('');

    try {
      await shareProject(project.id, email.trim());
      setShareSuccess(`Project shared successfully with ${email}`);
      setEmail('');
      loadAccessList(); // Refresh the access list
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      console.error('Error sharing project:', error);
      setShareError(error.message || 'Failed to share project');
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevokeAccess = async (email) => {
    if (!project?.id) return;

    setRevokingAccess(email);
    try {
      await revokeProjectAccess(project.id, email);
      loadAccessList(); // Refresh the access list
    } catch (error) {
      console.error('Error revoking access:', error);
      alert('Failed to revoke access. Please try again.');
    } finally {
      setRevokingAccess(null);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="share-project-modal-overlay" onClick={onClose}>
      <div className="share-project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-project-modal-header">
          <h2>Share Project</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="share-project-modal-body">
          <div className="project-info">
            <h3>{project?.name || 'Untitled Project'}</h3>
            <p className="project-customer">{project?.customer || 'Unknown Customer'}</p>
          </div>

          {/* Share Form */}
          <form onSubmit={handleShare} className="share-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={isSharing}
                required
              />
            </div>

            {shareError && (
              <div className="error-message">
                {shareError}
              </div>
            )}

            {shareSuccess && (
              <div className="success-message">
                {shareSuccess}
              </div>
            )}

            <button 
              type="submit" 
              className="share-button"
              disabled={isSharing || !email.trim()}
            >
              {isSharing ? 'Sharing...' : 'Share Project'}
            </button>
          </form>

          {/* Access List */}
          <div className="access-list-section">
            <h3>Current Access</h3>
            {isLoadingAccess ? (
              <div className="loading">Loading access list...</div>
            ) : accessList.length > 0 ? (
              <div className="access-list">
                {accessList.map((access, index) => (
                  <div key={index} className="access-item">
                    <div className="access-info">
                      <span className="access-email">{access.email}</span>
                      <span className="access-date">
                        Shared on {formatDate(access.sharedAt)}
                      </span>
                    </div>
                    <button
                      className="revoke-button"
                      onClick={() => handleRevokeAccess(access.email)}
                      disabled={revokingAccess === access.email}
                    >
                      {revokingAccess === access.email ? 'Revoking...' : 'Revoke'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-access">
                No one has access to this project yet.
              </div>
            )}
          </div>

          <div className="share-info">
            <p>
              <strong>Note:</strong> Shared users will have read-only access to this project. 
              They can view the project details, equipment, and conductors but cannot make changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProjectModal; 